import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

const HotelImageGallery = ({ images = [], hotelName = "Hotel" }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (index) => {
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };


  if (!images || images.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-16 text-center border border-gray-200">
        <div className="w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
          <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-neutral-900 mb-3 typography-subsection-title">No Images Available</h3>
        <p className="text-gray-600 text-lg">Images for this hotel are not currently available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Unified Gallery Grid - 2 Rows Only */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4">
        {images.slice(0, 10).map((image, index) => (
          <motion.div
            key={image.id || index}
            className="relative group cursor-pointer aspect-square"
            whileHover={{ scale: 1.03, y: -4 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => openModal(index)}
          >
            <div className="w-full h-full rounded-2xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 shadow-lg group-hover:shadow-xl transition-all duration-300">
              <img
                src={image.urls?.large || image.urls?.medium || image.urls?.small || image.Url || image.url}
                alt={image.caption || `Hotel image ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
            </div>
            
            {/* Elegant Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent group-hover:from-black/40 transition-all duration-300 rounded-2xl flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal(index);
                  }}
                  className="p-3 bg-white/95 backdrop-blur-sm rounded-xl hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <Maximize2 className="w-5 h-5 text-gray-700" />
                </button>
              </div>
            </div>

            {/* Image Counter */}
            {images.length > 10 && index === 9 && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent rounded-2xl flex items-center justify-center">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
                  <span className="text-white font-semibold text-sm">
                    +{images.length - 10} more
                  </span>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* View All Button */}
      {images.length > 10 && (
        <div className="text-center">
          <button
            onClick={() => openModal(0)}
            className="px-8 py-4 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 hover:text-gray-900 rounded-xl transition-all duration-300 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            View All {images.length} Images
          </button>
        </div>
      )}



      {/* Full Screen Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={closeModal}
          >
            <div className="relative max-w-7xl max-h-full w-full flex flex-col items-center justify-center min-h-0">
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 bg-white/10 hover:bg-white/20 text-white p-2 sm:p-3 rounded-full transition-all duration-200 backdrop-blur-sm"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>

              {/* Navigation Buttons */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prevImage();
                    }}
                    className="absolute left-1 sm:left-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-2 sm:p-3 rounded-full transition-all duration-200 backdrop-blur-sm"
                  >
                    <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextImage();
                    }}
                    className="absolute right-1 sm:right-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-2 sm:p-3 rounded-full transition-all duration-200 backdrop-blur-sm"
                  >
                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </>
              )}

              {/* Main Image */}
              <motion.img
                key={selectedImageIndex}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                src={images[selectedImageIndex]?.urls?.original || images[selectedImageIndex]?.urls?.large || images[selectedImageIndex]?.urls?.medium || images[selectedImageIndex]?.Url}
                alt={images[selectedImageIndex]?.caption || `Hotel image ${selectedImageIndex + 1}`}
                className="max-w-full max-h-[65dvh] sm:max-h-[80dvh] object-contain rounded-lg shadow-2xl mx-auto"
                onClick={(e) => e.stopPropagation()}
              />

              {/* Image Info */}
              <div className="absolute bottom-[5.5rem] sm:bottom-24 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm text-white p-3 sm:p-4 rounded-xl max-w-[calc(100vw-1.5rem)] sm:max-w-md text-center">
                <h3 className="font-semibold mb-1 text-sm sm:text-base line-clamp-2">
                  {images[selectedImageIndex]?.caption || images[selectedImageIndex]?.category || 'Hotel Image'}
                </h3>
                <p className="text-xs sm:text-sm text-white/80">
                  {selectedImageIndex + 1} of {images.length}
                </p>
              </div>


              {/* Thumbnail Strip */}
              {images.length > 1 && (
                <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 w-full max-w-[100vw] px-2">
                  <div className="flex gap-2 max-w-2xl mx-auto overflow-x-auto scrollbar-hide pb-1">
                    {images.map((image, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImageIndex(index);
                        }}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all duration-200 ${
                          index === selectedImageIndex
                            ? 'ring-2 ring-white scale-110'
                            : 'opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={image.urls?.small || image.urls?.medium || image.Url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HotelImageGallery;
